import { DateTime } from 'luxon';
import api, { adminApiPrefix } from './Api';
import {
  StockReportFilter,
  stockReportFilterToQueryParams,
} from '../interfaces/Report/StockReport/StockReportFilter';
import { PaginatedResponse } from '../interfaces/PaginatedResponse';
import {
  StockReport,
  stockReportFromResponse,
} from '../interfaces/Report/StockReport/StockReport';
import {
  SalesReportFilter,
  salesReportFilterToQueryParams,
} from '../interfaces/Report/SalesReport/SalesReportFilter';
import {
  SalesReport,
  salesReportFromResponse,
} from '../interfaces/Report/SalesReport/SalesReport';
import {
  CustomerOrdersReportFilter,
  customerOrdersReportFilterToQueryParams,
} from '../interfaces/Report/CustomerOrdersReport/CustomerOrdersReportFilter';
import {
  CustomerOrdersReport,
  customerOrdersReportFromResponse,
} from '../interfaces/Report/CustomerOrdersReport/CustomerOrdersReport';
import {
  PartnerPlatformSalesReports,
  partnerPlatformSalesReportsFromResponse,
} from '../interfaces/Report/PartnerPlatformSalesReport/PartnerPlatformSalesReport';
import {
  ProductSalesReportFilter,
  productSalesReportFilterToQueryParams,
} from '../interfaces/Report/ProductSalesReport/ProductSalesReportFilter';
import {
  ProductSalesReport,
  productSalesReportFromResponse,
} from '../interfaces/Report/ProductSalesReport/ProductSalesReport';
import {
  StockHistoryReportFilter,
  stockHistoryReportFilterToQueryParams,
} from '../interfaces/Report/StockHistoryReport/StockHistoryReportFilter';
import {
  StockHistory,
  stockHistoryFromResponse,
} from '../interfaces/Report/StockHistoryReport/StockHistory';

const reportAdminApiPrefix = `${adminApiPrefix}/report`;

export const getCustomerOrdersReport = async (
  filter: CustomerOrdersReportFilter,
): Promise<PaginatedResponse<CustomerOrdersReport>> => {
  const response = await api.get(`${reportAdminApiPrefix}/customer-orders`, {
    params: customerOrdersReportFilterToQueryParams(filter),
  });

  return {
    data: (response.data?.data ?? []).map((ordersReport: any) =>
      customerOrdersReportFromResponse(ordersReport),
    ),
    totalCount: response.data?.total_count ?? 0,
  };
};

export const getSalesReport = async (
  filter: SalesReportFilter,
): Promise<PaginatedResponse<SalesReport>> => {
  const response = await api.get(`${reportAdminApiPrefix}/sales`, {
    params: salesReportFilterToQueryParams(filter),
  });

  return {
    data: (response.data?.data ?? []).map((salesReport: any) =>
      salesReportFromResponse(salesReport),
    ),
    totalCount: response.data?.total_count ?? 0,
  };
};

export const getProductSalesReport = async (
  filter: ProductSalesReportFilter,
): Promise<PaginatedResponse<ProductSalesReport>> => {
  const response = await api.get(`${reportAdminApiPrefix}/product-sales`, {
    params: productSalesReportFilterToQueryParams(filter),
  });

  return {
    data: (response.data?.data ?? []).map((salesReport: any) =>
      productSalesReportFromResponse(salesReport),
    ),
    totalCount: response.data?.total_count ?? 0,
  };
};

export const getPartnerPlatformSalesReports = async (
  startAt: Date,
): Promise<PartnerPlatformSalesReports> => {
  const response = await api.get(
    `${reportAdminApiPrefix}/partner-platform-sales`,
    {
      params: {
        'start-at': DateTime.fromJSDate(startAt).toISO(),
      },
    },
  );

  return partnerPlatformSalesReportsFromResponse(response.data?.data);
};

export const getStockReport = async (
  filter: StockReportFilter,
): Promise<PaginatedResponse<StockReport>> => {
  const response = await api.get(`${reportAdminApiPrefix}/stock`, {
    params: stockReportFilterToQueryParams(filter),
  });

  return {
    data: (response.data?.data ?? []).map((stockReport: any) =>
      stockReportFromResponse(stockReport),
    ),
    totalCount: response.data?.total_count ?? 0,
  };
};

export const getStockHistoryReport = async (
  filter: StockHistoryReportFilter,
): Promise<PaginatedResponse<StockHistory>> => {
  const response = await api.get(`${reportAdminApiPrefix}/stock-history`, {
    params: stockHistoryReportFilterToQueryParams(filter),
  });

  return {
    data: (response.data?.data ?? []).map((stockHistory: any) =>
      stockHistoryFromResponse(stockHistory),
    ),
    totalCount: response.data?.total_count ?? 0,
  };
};
